import { useEffect, memo } from 'react';
import { geoCentroid } from 'd3-geo';
import {
  ComposableMap,
  Geographies,
  Geography,
  Marker,
  Annotation,
} from 'react-simple-maps';
import useSWR from 'swr';
import ReactTooltip from 'react-tooltip';

import { fetchResults } from '../api';
import allStates from '../data/allstates.json';
import * as constants from '../constants';

const geoUrl = 'https://cdn.jsdelivr.net/npm/us-atlas@3/states-10m.json';

const offsets = {
  VT: [50, -8],
  NH: [34, 2],
  MA: [30, -1],
  RI: [28, 2],
  CT: [35, 10],
  NJ: [34, 1],
  DE: [33, 0],
  MD: [47, 10],
  DC: [49, 21],
};

const MapChart = ({ setTooltipContent }) => {
  const {
    data: { races },
  } = useSWR('nytimes-results', fetchResults, {
    refreshInterval: 60000,
    refreshWhenHidden: true,
    suspense: true,
  });

  useEffect(() => {
    // This rebuild is needed for tooltip styles
    ReactTooltip.rebuild();
  }, []);

  const getStateWinnerColor = (geoId) => {
    const cur = allStates.find((s) => s.val === geoId);
    const race = races.find((i) => i.state_id === cur.id);

    if (!race) return;

    const winner = race.candidates.find((i) => i.winner);

    if (winner) {
      return winner.last_name === 'Trump' ? constants.RED : constants.BLUE;
    }

    const { last_name } = race.candidates.sort((a, b) => b.votes - a.votes)[0];
    return last_name === 'Trump' ? constants.LIGHT_RED : constants.LIGHT_BLUE;
  };

  const handleMouseEnter = (geoId) => {
    const cur = allStates.find((s) => s.val === geoId);
    const race = races.find((i) => i.state_id === cur.id);
    const winner = race.candidates.find((i) => i.winner);

    setTooltipContent({
      name: race.state_name,
      electTotal: race.electoral_votes,
      eevp: race.eevp,
      winner: winner ? winner.name_display : null,
      imgUrl: winner ? winner.img_url : null,
    });
    ReactTooltip.rebuild();
  };

  return (
    <ComposableMap data-tip="" projection="geoAlbersUsa">
      <Geographies geography={geoUrl}>
        {({ geographies }) => (
          <>
            {geographies.map((geo) => {
              const fillColor = getStateWinnerColor(geo.id);
              return (
                <Geography
                  key={geo.rsmKey}
                  stroke="#FFF"
                  geography={geo}
                  fill="#DDD"
                  style={{
                    default: {
                      fill: fillColor,
                      outline: 'none',
                    },
                    hover: {
                      fill: fillColor,
                      outline: 'none',
                    },
                    pressed: {
                      fill: fillColor,
                      outline: 'none',
                    },
                  }}
                  onMouseEnter={() => handleMouseEnter(geo.id)}
                  onMouseLeave={() => setTooltipContent(null)}
                />
              );
            })}
            {geographies.map((geo) => {
              const centroid = geoCentroid(geo);
              const cur = allStates.find((s) => s.val === geo.id);
              const fillColor = getStateWinnerColor(geo.id);
              return (
                <g key={geo.rsmKey + '-name'}>
                  {cur &&
                    centroid[0] > -160 &&
                    centroid[0] < -67 &&
                    (Object.keys(offsets).indexOf(cur.id) === -1 ? (
                      <Marker coordinates={centroid}>
                        <text
                          y="2"
                          fontSize={14}
                          textAnchor="middle"
                          fill={
                            fillColor === constants.LIGHT_RED ||
                            fillColor === constants.LIGHT_BLUE
                              ? '#000'
                              : '#FFF'
                          }
                        >
                          {cur.id}
                        </text>
                      </Marker>
                    ) : (
                      <Annotation
                        subject={centroid}
                        dx={offsets[cur.id][0]}
                        dy={offsets[cur.id][1]}
                      >
                        <text x={4} fontSize={14} alignmentBaseline="middle">
                          {cur.id}
                        </text>
                      </Annotation>
                    ))}
                </g>
              );
            })}
          </>
        )}
      </Geographies>
    </ComposableMap>
  );
};

export default memo(MapChart);
